/**
 * ============================================================================
 * useQuickCreatePrefs — Favoritos do botão "Novo" (Quick Create) no header
 * ----------------------------------------------------------------------------
 * Lê e persiste `quick_create_shortcuts.favorites` via RPCs
 * get_user_preferences / set_user_preferences com atualização otimista.
 * Usa uma chave de cache dedicada (`user-preferences-qc`) para NÃO conflitar
 * com os demais hooks de preferências.
 *
 * RELEASE: 2026-10-02 v1.1.0 - Mesma blindagem de useAppShortcutsPrefs v1.2.0:
 *   onSuccess grava o valor confirmado (sem refetch), toggle parte do cache
 *   mais recente e leitura incompleta não zera os favoritos.
 * RELEASE: 2026-07-24 v1.0.0 - Criação. Mesmo padrão de useAppShortcutsPrefs.
 * ============================================================================
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const log = createLogger('useQuickCreatePrefs');

/** Estrutura simples: apenas a lista de favoritos (order não é usada aqui). */
export interface QuickCreatePrefs {
  favorites: string[];
}

type CacheShape = { quick_create: QuickCreatePrefs };

const EMPTY: QuickCreatePrefs = { favorites: [] };
const QUERY_KEY = (userId?: string) => ['user-preferences-qc', userId] as const;

export function useQuickCreatePrefs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const key = QUERY_KEY(user?.id);

  const query = useQuery({
    queryKey: key,
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CacheShape> => {
      const { data, error } = await (supabase.rpc as any)('get_user_preferences');
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      const raw = row?.quick_create_shortcuts;
      // v1.1.0: leitura incompleta não zera o que a tela já tem.
      if (!Array.isArray(raw?.favorites)) {
        const previous = queryClient.getQueryData<CacheShape>(key);
        log.warn('Leitura sem quick_create_shortcuts válido; mantendo cache anterior');
        return previous ?? { quick_create: EMPTY };
      }
      return {
        quick_create: {
          favorites: raw.favorites.filter((x: unknown): x is string => typeof x === 'string' && x.length > 0),
        },
      };
    },
  });

  const prefs: QuickCreatePrefs = query.data?.quick_create ?? EMPTY;

  const mutation = useMutation({
    mutationFn: async (next: QuickCreatePrefs) => {
      const { error } = await (supabase.rpc as any)('set_user_preferences', {
        p_prefs: { quick_create_shortcuts: next },
      });
      if (error) throw error;
      return next;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CacheShape>(key);
      queryClient.setQueryData<CacheShape>(key, { quick_create: next });
      return { previous };
    },
    onError: (err: Error, _next, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(key, ctx.previous);
      }
      log.error('Falha ao salvar favoritos do Quick Create', { error: err });
      toast.error(`Erro ao salvar favoritos: ${err.message}`);
      // Só relê do servidor em caso de erro, para ressincronizar.
      queryClient.invalidateQueries({ queryKey: key });
    },
    onSuccess: (saved) => {
      // v1.1.0: grava o valor confirmado — sem refetch (evita corrida).
      queryClient.setQueryData<CacheShape>(key, { quick_create: saved });
    },
  });

  const toggleFavorite = useCallback(
    (actionKey: string) => {
      // Parte do estado mais recente (inclui cliques anteriores ainda salvando).
      const now = queryClient.getQueryData<CacheShape>(QUERY_KEY(user?.id))?.quick_create ?? EMPTY;
      const isFav = now.favorites.includes(actionKey);
      const nextFavorites = isFav
        ? now.favorites.filter((k) => k !== actionKey)
        : [...now.favorites, actionKey];
      mutation.mutate({ favorites: nextFavorites });
    },
    [queryClient, user?.id, mutation],
  );

  return {
    loading: query.isLoading,
    favorites: prefs.favorites,
    isFavorite: (k: string) => prefs.favorites.includes(k),
    toggleFavorite,
    saving: mutation.isPending,
  };
}
